export function calculateMonthlyBumpAmount(oneTimeAmount: number | string): number {
  const amount = parseFloat(String(oneTimeAmount));
  if (!amount || amount <= 0) return 10;
  if (amount <= 25) return 10;
  if (amount <= 50) return 15;
  if (amount <= 100) return 25;
  if (amount <= 250) return 50;
  return Math.round((amount * 0.2) / 10) * 10;
}

export const monthlyBumpMetadataFields = [
  'monthlyBumpAccepted',
  'monthlyBumpOriginalFrequency',
  'monthlyBumpOriginalAmount',
  'monthlyBumpAmount',
  'monthlyBumpConvertedAmount',
] as const;

type MonthlyBumpMetadata = Record<string, unknown> & {
  monthlyBumpAccepted?: boolean;
};

const hasValue = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '';

export function buildMonthlyBumpAcceptedMetadata({
  originalAmount,
  monthlyAmount,
  originalFrequency = 'ONE_TIME',
}: {
  originalAmount: number | string;
  monthlyAmount: number | string;
  originalFrequency?: string;
}): MonthlyBumpMetadata {
  return {
    monthlyBumpAccepted: true,
    monthlyBumpOriginalFrequency: originalFrequency,
    monthlyBumpOriginalAmount: originalAmount,
    monthlyBumpAmount: monthlyAmount,
  };
}

export function getMonthlyBumpMetadata(item: MonthlyBumpMetadata | null | undefined): MonthlyBumpMetadata {
  if (item?.monthlyBumpAccepted !== true) {
    return {};
  }

  return monthlyBumpMetadataFields.reduce<MonthlyBumpMetadata>((metadata, field) => {
    if (field === 'monthlyBumpAccepted') {
      return metadata;
    }
    if (hasValue(item[field])) {
      metadata[field] = item[field];
    }
    return metadata;
  }, { monthlyBumpAccepted: true });
}

export function mergeMonthlyBumpMetadata<T extends MonthlyBumpMetadata>(
  target: T,
  source: MonthlyBumpMetadata | null | undefined,
): T {
  const result = { ...target } as T & MonthlyBumpMetadata;
  const targetAccepted = result.monthlyBumpAccepted === true;
  const sourceAccepted = source?.monthlyBumpAccepted === true;

  if (!targetAccepted && !sourceAccepted) {
    return result;
  }

  result.monthlyBumpAccepted = true;
  const sourceMetadata = sourceAccepted ? getMonthlyBumpMetadata(source) : {};

  monthlyBumpMetadataFields.forEach((field) => {
    if (field === 'monthlyBumpAccepted') return;
    if (!hasValue(result[field]) && hasValue(sourceMetadata[field])) {
      (result as MonthlyBumpMetadata)[field] = sourceMetadata[field];
    }
  });

  return result;
}

export function applyMonthlyBumpMetadataToDonationData<T extends MonthlyBumpMetadata & {
  cartItems?: Record<string, unknown>[];
  crowdfundingOption?: MonthlyBumpMetadata;
  data?: (MonthlyBumpMetadata & { cartItems?: Record<string, unknown>[] }) | null;
}>(
  data: T,
  metadata: MonthlyBumpMetadata,
): T {
  if (metadata.monthlyBumpAccepted !== true) {
    return data;
  }

  // Top-level detail fields are a transient handoff for legacy hosted-script cart
  // creation. Checkout/backend durability should come from funds/cartItems.
  const result = Object.assign({ ...data }, metadata) as T;

  if (Array.isArray(result.cartItems)) {
    result.cartItems = result.cartItems.map((item) => ({
      ...item,
      ...metadata,
    }));
  }

  if (result.crowdfundingOption && typeof result.crowdfundingOption === 'object') {
    result.crowdfundingOption = {
      ...result.crowdfundingOption,
      ...metadata,
    };
  }

  if (result.data && typeof result.data === 'object') {
    if (Array.isArray(result.data.cartItems)) {
      result.data = {
        ...result.data,
        cartItems: result.data.cartItems.map((item) => ({
          ...item,
          ...metadata,
        })),
      };
    }
  }

  return result;
}
